import { Suspense, useEffect } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import {
  RouterProvider,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { CssBaseline, StyledEngineProvider, ThemeProvider } from '@mui/material';
import * as Sentry from '@sentry/react';
import { createTheme } from 'greener-ui-library';
import { SnackbarProvider } from 'notistack';
import ErrorFallback from './components/fallback/ErrorFallback';
import { LoadingScreen } from './components/fallback/LoadingScreen';
import { ProductionAmplitudeProvider } from './contexts/AmplitudeContext';
import { AuthProvider } from './contexts/Auth0Context';
import { SidebarProvider } from './contexts/SidebarContext';
import { UnleashProvider } from './contexts/UnleashContext';
import useSettings from './hooks/useSettings';
import { browserRouter } from './routes';
import { getBeforeSendConfig } from './utils/sentryUtils';
import { yupValidationMessages } from './utils/yupValidations';
import '@fontsource-variable/source-sans-3';

declare global {
  interface Window {
    __SENTRY_INITIALIZED__?: boolean;
    __REACT_DEVTOOLS_GLOBAL_HOOK__?: {
      inject: () => void;
    };
  }
}

if (!window.__SENTRY_INITIALIZED__ && import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.replayIntegration(),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_VERSION,

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    tracesSampleRate: 0.1,

    // This sets the sample rate. You may want this to be 100% while in development and sample at a lower rate in production
    replaysSessionSampleRate: ['staging', 'development'].includes(import.meta.env.MODE) ? 0 : 0.075,

    // If the entire session is not sampled, use the below sample rate to sample sessions when an error occurs.
    replaysOnErrorSampleRate: ['staging', 'development'].includes(import.meta.env.MODE) ? 0 : 0.75,
    beforeSend: getBeforeSendConfig,
  });
  window.__SENTRY_INITIALIZED__ = true;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      suspense: true,
      refetchOnWindowFocus: false,
      staleTime: 15 * 1000, // ms
      retry: 3,
    },
  },
});

/* Disable react devtools in production. */
if (import.meta.env.MODE !== 'development' && typeof window.__REACT_DEVTOOLS_GLOBAL_HOOK__ === 'object') {
  window.__REACT_DEVTOOLS_GLOBAL_HOOK__.inject = function () {};
}

export default function App() {
  const { settings } = useSettings();
  const theme = createTheme({
    direction: settings.direction,
    responsiveFontSizes: settings.responsiveFontSizes,
    theme: settings.theme,
  });
  yupValidationMessages();

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ProductionAmplitudeProvider>
          <Sentry.ErrorBoundary fallback={ErrorFallback}>
            <HelmetProvider>
              <SnackbarProvider dense maxSnack={3}>
                <QueryClientProvider client={queryClient}>
                  <UnleashProvider>
                    <AuthProvider>
                      <SidebarProvider>
                        <Helmet defaultTitle="Greener Projects" titleTemplate="%s | Greener Projects" />
                        <Suspense fallback={<LoadingScreen />}>
                          <RouterProvider router={browserRouter} fallbackElement={<LoadingScreen />} />
                        </Suspense>
                      </SidebarProvider>
                    </AuthProvider>
                  </UnleashProvider>
                  <ReactQueryDevtools initialIsOpen={false} />
                </QueryClientProvider>
              </SnackbarProvider>
            </HelmetProvider>
          </Sentry.ErrorBoundary>
        </ProductionAmplitudeProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
